import {useState} from "react";
import {Box, IconButton, TextField, Tooltip} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {clampAngleDeg, MAX_ANGLE_DEG, MIN_ANGLE_DEG} from "../scenes/frameGrid/BlockOps";

const DIAL_SIZE = 44;
const DIAL_RADIUS = DIAL_SIZE / 2 - 3;

// A degrees editor for an angled stick: a number field, a dial whose arm drags the
// angle, and a remove button that straightens the stick. The dial shows the plan
// angle the way the top-down section does: the fixed arm is the run on the left, the
// draggable arm is the run on the right, and the angle opens toward the bottom.
export function RibbonAngle(props: {
    value: number;
    onChange: (deg: number) => void;
    onRemove: () => void;
}) {
    // the field keeps the user's partial text until it parses; the dial and model
    // always show the committed value.
    const [text, setText] = useState<string | null>(null);

    const commit = (deg: number) => {
        if(isNaN(deg)) return;
        props.onChange(Math.round(clampAngleDeg(deg)));
    };

    return (
        <Box sx={{display: "flex", alignItems: "center", height: "100%", px: 0.5, gap: 0.5}}>
            <TextField
                size="small"
                type="number"
                value={text ?? props.value}
                inputProps={{min: MIN_ANGLE_DEG, max: MAX_ANGLE_DEG, step: 1, style: {width: 48, padding: "4px 8px", fontSize: 13}}}
                InputProps={{endAdornment: <Box component="span" sx={{fontSize: 12, color: "text.secondary", pr: 0.5}}>°</Box>}}
                onChange={e => {
                    setText(e.target.value);
                    commit(parseFloat(e.target.value));
                }}
                onBlur={() => setText(null)}
            />
            <AngleDial value={props.value} onChange={commit} />
            <Tooltip title="Remove angle" arrow>
                <IconButton size="small" sx={{p: 0.25}} onClick={props.onRemove}>
                    <CloseIcon sx={{fontSize: 18}} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// the right run's direction on the dial for a plan angle: straight (180°) points right,
// smaller angles swing it down (toward the locationOne side), larger ones up.
function armDirection(deg: number): {x: number; y: number} {
    const rad = deg * Math.PI / 180;
    return {x: -Math.cos(rad), y: Math.sin(rad)};
}

function pointerAngle(dx: number, dy: number): number {
    const deg = Math.atan2(dy, -dx) * 180 / Math.PI;
    return deg < 0 ? deg + 360 : deg;
}

function AngleDial(props: {value: number; onChange: (deg: number) => void}) {
    const c = DIAL_SIZE / 2;
    const arm = armDirection(props.value);

    const onPointer = (e: React.PointerEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const dx = e.clientX - rect.left - c;
        const dy = e.clientY - rect.top - c;
        if(Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
        props.onChange(pointerAngle(dx, dy));
    };

    return (
        <Tooltip title="Drag to set the angle" arrow>
            <svg
                width={DIAL_SIZE}
                height={DIAL_SIZE}
                style={{cursor: "grab", touchAction: "none", flexShrink: 0}}
                onPointerDown={e => {
                    e.currentTarget.setPointerCapture(e.pointerId);
                    onPointer(e);
                }}
                onPointerMove={e => {
                    if(e.buttons === 0) return;
                    onPointer(e);
                }}
            >
                <circle cx={c} cy={c} r={DIAL_RADIUS} fill="none" stroke="currentColor" strokeOpacity={0.3} />
                <line x1={c} y1={c} x2={c - DIAL_RADIUS} y2={c} stroke="currentColor" strokeOpacity={0.4} strokeWidth={2} />
                <line x1={c} y1={c} x2={c + arm.x * DIAL_RADIUS} y2={c + arm.y * DIAL_RADIUS} stroke="currentColor" strokeWidth={2} />
                <circle cx={c + arm.x * DIAL_RADIUS} cy={c + arm.y * DIAL_RADIUS} r={3.5} fill="currentColor" />
            </svg>
        </Tooltip>
    );
}
